'use client';

import type React from 'react';
import { memo, useMemo, useRef } from 'react';
import type * as HighchartsTypes from 'highcharts';
import dynamic from 'next/dynamic';
import { useThemeMode } from '@terra/ui-design-system';
import { token } from '@terra/ui-styled-system';
import worldTopology from './maps/world.topo.json';

export type WorldChoroplethPoint = {
  id: string;
  value: number;
  name?: string;
};

export type WorldChoroplethChartProps = {
  data: WorldChoroplethPoint[];
  height?: number | string;
  onSelect?: (countryCode: string) => void;
};

const ChartComponent = dynamic(
  async () => {
    const HighchartsModule = await import('highcharts');

    await import('highcharts/modules/map');
    const { default: HighchartsReact } = await import(
      'highcharts-react-official'
    );

    return ({ options }: { options: HighchartsTypes.Options }) => {
      const Highcharts =
        (HighchartsModule as { default?: typeof HighchartsTypes }).default ??
        HighchartsModule;

      return (
        <HighchartsReact
          highcharts={Highcharts}
          constructorType="mapChart"
          options={options}
        />
      );
    };
  },
  {
    ssr: false,
    loading: () => null,
  },
) as React.ComponentType<{ options: HighchartsTypes.Options }>;

function getMapColors(theme: 'light' | 'dark') {
  if (theme === 'dark') {
    return {
      nullColor: token('colors.bg.muted'),
      minColor: token('colors.bg.emphasized'),
      maxColor: token('colors.bg.graphs.sky'),
      borderColor: token('colors.border.muted'),
    };
  }

  return {
    nullColor: token('colors.bg.subtle'),
    minColor: token('colors.bg.muted'),
    maxColor: token('colors.bg.graphs.sky'),
    borderColor: token('colors.border.subtle'),
  };
}

export function toWorldChoroplethSeriesData(data: WorldChoroplethPoint[]) {
  return data.map((point) => ({
    id: point.id.toUpperCase(),
    value: point.value,
    name: point.name,
  }));
}

export const WorldChoroplethChart = memo(function WorldChoroplethChart({
  data,
  height = 240,
  onSelect,
}: WorldChoroplethChartProps) {
  const { mode: themeMode } = useThemeMode();
  const onSelectRef = useRef(onSelect);

  onSelectRef.current = onSelect;
  const colors = getMapColors(themeMode ?? 'light');
  const seriesData = useMemo(() => toWorldChoroplethSeriesData(data), [data]);

  const options = useMemo(
    () => ({
      chart: {
        map: worldTopology,
        backgroundColor: 'transparent',
        height,
        animation: false,
        style: { fontFamily: 'inherit' },
      },
      title: { text: undefined },
      credits: { enabled: false },
      legend: { enabled: false },
      colorAxis: {
        min: 0,
        minColor: colors.minColor,
        maxColor: colors.maxColor,
      },
      tooltip: {
        backgroundColor: token('colors.bg.default'),
        borderWidth: 0,
        borderRadius: 4,
        style: {
          color: token('colors.fg.default'),
          fontSize: '12px',
        },
      },
      plotOptions: {
        series: {
          animation: false,
        },
      },
      series: [
        {
          type: 'map' as const,
          mapData: worldTopology,
          joinBy: ['iso-a2', 'id'],
          data: seriesData,
          nullColor: colors.nullColor,
          borderColor: colors.borderColor,
          borderWidth: 0.5,
          animation: false,
          states: {
            hover: {
              brightness: 0.08,
            },
          },
          point: {
            events: {
              click() {
                const selected = this as {
                  id?: string;
                  value?: number | null;
                };
                const countryCode = selected.id;

                if (countryCode && selected.value) {
                  onSelectRef.current?.(countryCode.toUpperCase());
                }
              },
            },
          },
        },
      ],
    }),
    [
      colors.borderColor,
      colors.maxColor,
      colors.minColor,
      colors.nullColor,
      height,
      seriesData,
    ],
  );

  return <ChartComponent options={options as HighchartsTypes.Options} />;
});
